/**
 * Renders a card with proper suit display and color
 * @param {string} cardCode - The card code (e.g., "AH" for Ace of Hearts)
 * @param {string} variant - Optional variant for styling
 * @returns {JSX.Element} The card display element
 */
export const getCardDisplay = (cardCode, variant = '') => {
  const cardValue = cardCode.slice(0, -1)
  const suit = cardCode.slice(-1)
  let suitSymbol = ''
  let suitClass = ''

  // Apply special styling for buttons
  if (variant === 'button') {
    suitClass = 'text-white text-xl'
  } else {
    // Apply appropriate color based on suit
    switch (suit) {
      case 'C':
        suitClass = 'text-black'
        break
      case 'H':
        suitClass = 'text-red-500'
        break
      case 'S':
        suitClass = 'text-black'
        break
      case 'D':
        suitClass = 'text-red-500'
        break
      default:
        suitClass = ''
    }
  }

  // Convert suit letter to unicode symbol
  switch (suit) {
    case 'C':
      suitSymbol = '♣'
      break
    case 'H':
      suitSymbol = '♥'
      break
    case 'S':
      suitSymbol = '♠'
      break
    case 'D':
      suitSymbol = '♦'
      break
    default:
      suitSymbol = suit
  }

  return (
    <span>
      {cardValue} <span className={suitClass}>{suitSymbol}</span>
    </span>
  )
}

/**
 * Shuffles an array using Fisher-Yates algorithm
 * @param {Array} array - The array to shuffle
 * @returns {Array} A new shuffled array
 */
export const shuffleArray = array => {
  const arr = [...array]
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

/**
 * Creates a CSS keyframe animation for shaking elements
 * @returns {string} CSS string for the shake animation
 */
export const getShakeAnimationStyle = () => `
@keyframes shake {
  0% { transform: translateX(0) }
  25% { transform: translateX(-5px) }
  50% { transform: translateX(5px) }
  75% { transform: translateX(-5px) }
  100% { transform: translateX(0) }
}
.shake-animation {
  animation: shake 0.5s ease-in-out
}
`

/**
 * Function to save settings to localStorage
 * @param {string} storageKey - The key to use for localStorage
 * @param {Object} settings - The settings object to save
 */
export const saveSettingsToStorage = (storageKey, settings) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(settings))
  } catch (error) {
    console.error('Error saving settings to localStorage:', error)
  }
}

/**
 * Function to load settings from localStorage
 * @param {string} storageKey - The key to use for localStorage
 * @returns {Object|null} The settings object or null if not found
 */
export const loadSettingsFromStorage = storageKey => {
  try {
    const storedSettings = localStorage.getItem(storageKey)
    return storedSettings ? JSON.parse(storedSettings) : null
  } catch (error) {
    console.error('Error loading settings from localStorage:', error)
    return null
  }
}
